import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart, MapPin, Star, Inbox } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import s1 from "@/assets/space-1.webp";
import s2 from "@/assets/space-2.webp";
import s3 from "@/assets/space-3.webp";
import s4 from "@/assets/space-4.webp";
import s5 from "@/assets/space-5.webp";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/favorites")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Your favorites — Spayzo" },
      { name: "description", content: "Spaces you saved for later on Spayzo." },
      { property: "og:title", content: "Your favorites — Spayzo" },
      { property: "og:description", content: "Quickly revisit and book the spaces you love." },
    ],
  }),
  component: FavoritesPage,
});

type Fav = {
  id: string;
  img: string;
  title: string;
  city: string;
  price: number;
  rating: number;
  reviews: number;
};

const INITIAL: Fav[] = [
  { id: "1", img: s1, title: "Industrial loft with brick walls", city: "Brooklyn, NY", price: 64, rating: 4.92, reviews: 128 },
  { id: "2", img: s2, title: "Pastel podcast studio", city: "Austin, TX", price: 38, rating: 4.87, reviews: 94 },
  { id: "3", img: s3, title: "Skyline rooftop terrace", city: "Lisbon, PT", price: 95, rating: 5.0, reviews: 41 },
  { id: "4", img: s4, title: "Velvet lounge meeting room", city: "Paris, FR", price: 52, rating: 4.95, reviews: 212 },
  { id: "5", img: s5, title: "Minimal gallery space", city: "Berlin, DE", price: 78, rating: 4.89, reviews: 67 },
];

function FavoritesPage() {
  const { t } = useTranslation();
  const [favorites, setFavorites] = useState<Fav[]>(INITIAL);

  const remove = (id: string) =>
    setFavorites((prev) => prev.filter((f) => f.id !== id));

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SiteHeader />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              {t("pages.favorites.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("pages.favorites.saved", { count: favorites.length })}
            </p>
          </div>
          <Button asChild variant="outline" className="hidden sm:inline-flex">
            <Link to="/dashboard">{t("pages.favorites.back")}</Link>
          </Button>
        </div>

        {favorites.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed py-20 text-center">
            <Inbox className="h-10 w-10 text-muted-foreground" />
            <h2 className="mt-3 font-display text-lg font-semibold">
              {t("pages.favorites.emptyTitle")}
            </h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {t("pages.favorites.emptyBody")}
            </p>
            <Button asChild className="mt-5">
              <Link to="/discover">{t("pages.favorites.discover")}</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {favorites.map((f) => (
              <FavoriteCard key={f.id} fav={f} onRemove={() => remove(f.id)} />
            ))}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function FavoriteCard({ fav, onRemove }: { fav: Fav; onRemove: () => void }) {
  const { t } = useTranslation();
  const title = t(`pages.favorites.demo.s${fav.id}.title`, { defaultValue: fav.title });
  const city = t(`pages.favorites.demo.s${fav.id}.city`, { defaultValue: fav.city });
  return (
    <div className="group overflow-hidden rounded-2xl border bg-card shadow-soft transition-shadow hover:shadow-card">
      <Link
        to="/spaces/$spaceId"
        params={{ spaceId: fav.id }}
        className="relative block aspect-[4/3] overflow-hidden"
      >
        <img
          src={fav.img}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
                decoding="async"
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className={cn(
            "absolute end-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full",
            "bg-background/90 text-rose-500 shadow-soft backdrop-blur transition hover:bg-background",
          )}
          aria-label={t("pages.favorites.remove")}
        >
          <Heart className="h-4 w-4 fill-current" />
        </button>
      </Link>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <Link
            to="/spaces/$spaceId"
            params={{ spaceId: fav.id }}
            className="line-clamp-1 font-medium hover:underline"
          >
            {title}
          </Link>
          <span className="flex shrink-0 items-center gap-1 text-sm">
            <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
            {fav.rating}
          </span>
        </div>
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          {city}
        </p>
        <div className="mt-3 flex items-end justify-between">
          <p className="text-sm">
            <span className="font-semibold"><Riyal /> {fav.price}</span>
            <span className="text-muted-foreground"> {t("pages.favorites.perHour")}</span>
          </p>
          <Button asChild size="sm" variant="outline" className="rounded-full">
            <Link to="/spaces/$spaceId" params={{ spaceId: fav.id }}>
              {t("pages.favorites.book")}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
